import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import imageCompression from 'browser-image-compression';

const DEFAULT_COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  initialQuality: 0.8,
  useWebWorker: true,
  fileType: 'image/jpeg',
};

const COMPRESSIBLE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);

const EXT_TO_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
};

function inferMimeType(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  return EXT_TO_MIME[ext] || 'application/octet-stream';
}

function replaceExtension(filename, newExt) {
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? `${filename}.${newExt}` : `${filename.slice(0, lastDot)}.${newExt}`;
}

function isCompressible(file) {
  const type = file.type || inferMimeType(file.name);
  return COMPRESSIBLE_TYPES.has(type);
}

async function compressImage(file, options) {
  if (!isCompressible(file)) {
    return {
      blob: file,
      filename: file.name,
      contentType: file.type || inferMimeType(file.name),
    };
  }
  try {
    const compressed = await imageCompression(file, { ...DEFAULT_COMPRESSION_OPTIONS, ...options });
    const contentType = compressed.type || 'image/jpeg';
    const filename = contentType === 'image/jpeg' ? replaceExtension(file.name, 'jpg') : file.name;
    return { blob: compressed, filename, contentType };
  } catch (err) {
    console.warn('Image compression failed, uploading original:', err); // eslint-disable-line no-console
    return {
      blob: file,
      filename: file.name,
      contentType: file.type || inferMimeType(file.name),
    };
  }
}

export async function compressImagesForUpload(files, options, onProgress) {
  const total = files.length;
  let done = 0;
  if (onProgress) onProgress(0, total);
  return Promise.all(files.map(async (f) => {
    const result = await compressImage(f, options);
    done++;
    if (onProgress) onProgress(done, total);
    return result;
  }));
}

export function uploadToPresignedUrl(uploadUrl, blob, contentType) {
  // Native iOS app: Capacitor's native HTTP can send File bodies but not plain Blobs
  // (it falls back to JSON and fails), so wrap the compressed image in a File.
  const body = Capacitor.isNativePlatform() && !(blob instanceof File)
    ? new File([blob], 'upload', { type: contentType })
    : blob;
  return axios.put(uploadUrl, body, { headers: { 'Content-Type': contentType } });
}
